import React, { useState } from 'react';
import {
    Box, Button, Typography, Chip,
} from '@mui/material';
import { PANEL_TABS, PACKAGE_INFO } from '../constants/cpa.constants';
import CpaAssessmentPreview from './CpaAssessmentPreview.comp';
import SampleDashboardImg from '../../../../assets/images/Sample_partner_dash.png';
import { useColorMode } from '../../../../context/colorMode/colorMode.context';

// ── Left package card ─────────────────────────────────────────────────────────

const PackageCard = ({ pkg }) => {
    const { isDark } = useColorMode();
    const isLight = !!(pkg.cardBgColor && pkg.cardBgColor.toLowerCase() !== '#004aab');
    const accent = pkg.accentColor || '#004aab';
    // In dark mode use a dark surface with a colored left accent border instead of light pastels
    const bgColor = isDark ? 'var(--brand-surface, #1E1E1E)' : (pkg.cardBgColor || '#004aab');

    return (
        <Box sx={{
            width: 500,
            flexShrink: 0,
            bgcolor: bgColor,
            borderLeft: isDark ? `4px solid ${accent}` : 'none',
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
            overflowY: 'auto',
            borderRight: '1px solid var(--brand-border, #E5E7EB)',
        }}>
            {/* Tag chips */}
            {pkg.tags && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {pkg.tags.map((tag) => (
                        <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            sx={{
                                bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#fff',
                                color: isDark ? 'var(--brand-text, #E5E7EB)' : (accent),
                                fontWeight: 600,
                                fontSize: 11,
                                height: 26,
                                border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid #D1D5DB',
                                borderRadius: 1,
                            }}
                        />
                    ))}
                </Box>
            )}

            <Typography sx={{
                fontWeight: 800,
                fontSize: 28,
                lineHeight: 1.2,
                color: isDark ? 'var(--brand-text, #E5E7EB)' : (isLight ? accent : '#fff'),
            }}>
                {pkg.title}
            </Typography>

            {pkg.subtitle && (
                <Typography sx={{
                    fontSize: 16,
                    color: isDark ? 'var(--brand-text-muted, #9CA3AF)' : (isLight ? accent : 'rgba(255,255,255,0.88)'),
                    lineHeight: 1.55,
                    fontWeight: 600,
                }}>
                    {pkg.subtitle}
                </Typography>
            )}

            {pkg.description && (
                <Typography sx={{
                    fontSize: 14,
                    color: isDark ? 'var(--brand-text-muted, #9CA3AF)' : (isLight ? '#4B5563' : 'rgba(255,255,255,0.65)'),
                    lineHeight: 1.7,
                }}>
                    {pkg.description}
                </Typography>
            )}

            {/* Optional in-card CTA button (e.g. "Try Learning Creator") */}
            {pkg.cardCta && (
                <Button
                    variant="contained"
                    fullWidth
                    disableElevation
                    sx={{
                        textTransform: 'none',
                        bgcolor: isDark ? accent : '#111',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: 15,
                        py: 1.5,
                        borderRadius: 2,
                        '&:hover': { bgcolor: isDark ? accent : '#333', opacity: isDark ? 0.85 : 1 },
                    }}
                >
                    {pkg.cardCta.label}
                </Button>
            )}

            {/* Rich highlights (icon + title + desc) OR plain feature boxes */}
            {pkg.highlights ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, mt: 0.5 }}>
                    {pkg.highlights.map((h) => (
                        <Box key={h.title} sx={{
                            bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'var(--brand-surface, #fff)',
                            border: '1px solid var(--brand-border, #E5E7EB)',
                            borderRadius: 2,
                            px: 2.5,
                            py: 1.5,
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.5,
                        }}>
                            <Typography sx={{ fontSize: 15, lineHeight: 1.6, flexShrink: 0 }}>
                                {h.icon}
                            </Typography>
                            <Box>
                                <Typography sx={{ fontWeight: 700, fontSize: 14, color: 'var(--brand-text, #111)', lineHeight: 1.35 }}>
                                    {h.title}
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: 'var(--brand-text-muted, #6B7280)', lineHeight: 1.45 }}>
                                    {h.desc}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            ) : pkg.features ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, mt: 0.5 }}>
                    {pkg.features.map((f) => (
                        <Box key={f} sx={{
                            bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.12)',
                            border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.18)',
                            borderRadius: 2,
                            px: 2.5,
                            py: 1.3,
                        }}>
                            <Typography sx={{ fontWeight: 600, fontSize: 14, color: isDark ? 'var(--brand-text, #E5E7EB)' : '#fff', lineHeight: 1.5 }}>
                                {f}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            ) : null}
        </Box>
    );
};

// ── Main component ────────────────────────────────────────────────────────────

const CpaPackagePanel = ({ visible, packageCode, onRequestQuote, showQuoteButton, onTabChange }) => {
    const [tab, setTab] = useState(0);
    const { isDark } = useColorMode();

    const handleTabChange = (idx) => {
        setTab(idx);
        if (onTabChange) onTabChange(idx);
    };

    if (!visible) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 4 }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'var(--brand-text, #4a4a4a)', textAlign: 'center', lineHeight: 1.3 }}>
                    Let&apos;s find the best package<br />that fits your needs.
                </Typography>
            </Box>
        );
    }

    const pkg = PACKAGE_INFO[packageCode] || null;
    const headerTitle = pkg ? pkg.title : 'Powered by DQ solutions';
    const headerSubtitle = pkg
        ? pkg.subtitle
        : 'Assess, align, build, and scale AI-digital literacy programs';

    const activeTabs = (pkg && pkg.tabs) || PANEL_TABS;

    return (
        <Box sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: 'var(--brand-bg, #F3F4F6)',
        }}>
            {/* ── White card ───────────────────────────────────────────────── */}
            <Box sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                bgcolor: 'var(--brand-surface, #fff)',
                borderRadius: 3,
                boxShadow: '0 1px 12px rgba(0,0,0,0.07)',
                border: '1px solid var(--brand-border, #E5E7EB)',
            }}>

                {/* ── Title + tabs — single row ────────────────────────── */}
                <Box sx={{
                    px: 3, py: 2,
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid var(--brand-border, #F0F0F0)',
                    gap: 2,
                }}>
                    {/* Title row: centered title+chip, button pinned right */}
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        {/* Left spacer — balances the button on the right */}
                        <Box sx={{ flex: 1 }} />
                        {/* Center: title + package ID chip */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                            <Typography sx={{ fontWeight: 800, fontSize: 25, color: 'var(--brand-text, #111)', mt: 2, mb: 1, lineHeight: 1.25 }}>
                                {headerTitle}
                            </Typography>
                            {pkg && (
                                <Chip
                                    label={pkg.id}
                                    size="small"
                                    sx={{
                                        bgcolor: '#FF6900',
                                        color: '#fff',
                                        fontWeight: 700,
                                        fontSize: 11,
                                        height: 22,
                                        borderRadius: 1,
                                        flexShrink: 0,
                                    }}
                                />
                            )}
                        </Box>
                        {/* Right: Request Proposal button */}
                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                            {showQuoteButton && onRequestQuote && tab === 0 && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={onRequestQuote}
                                    sx={{
                                        textTransform: 'none',
                                        borderColor: '#FF6900',
                                        color: '#FF6900',
                                        fontWeight: 600,
                                        fontSize: 14,
                                        flexShrink: 0,
                                        whiteSpace: 'nowrap',
                                        borderRadius: 1.5,
                                        px: 2,
                                        '&:hover': { bgcolor: '#FFF3ED', borderColor: '#E65C00' },
                                    }}
                                >
                                    Request Proposal
                                </Button>
                            )}
                        </Box>
                    </Box>

                    {/* Right: tab pill group */}
                    <Box sx={{
                        display: 'inline-flex',
                        flexShrink: 0,
                        bgcolor: 'var(--brand-bg, #F3F4F6)',
                        borderRadius: 2,
                        p: 0.5,
                        gap: 0.5,
                    }}>
                        {activeTabs.map((t, idx) => (
                            <Button
                                key={t.id}
                                disableElevation
                                onClick={() => handleTabChange(idx)}
                                sx={{
                                    bgcolor: tab === idx ? '#FF6900' : 'transparent',
                                    color: tab === idx ? '#fff' : 'var(--brand-text-muted, #6B7280)',
                                    fontWeight: tab === idx ? 700 : 500,
                                    fontSize: 13,
                                    textTransform: 'none',
                                    borderRadius: 1.5,
                                    px: 2.5,
                                    py: 0.8,
                                    minHeight: 0,
                                    lineHeight: 1.4,
                                    whiteSpace: 'normal',
                                    textAlign: 'center',
                                    maxWidth: 200,
                                    boxShadow: tab === idx ? '0 1px 4px rgba(255,105,0,0.25)' : 'none',
                                    '&:hover': {
                                        bgcolor: tab === idx ? '#E65C00' : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                                    },
                                }}
                            >
                                {t.label}
                            </Button>
                        ))}
                    </Box>
                </Box>

                {/* ── Two-column layout ─────────────────────────────────── */}
                <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

                    {/* Assessment preview — full width, own background */}
                    {tab === 1 && <CpaAssessmentPreview onExit={() => handleTabChange(0)} />}

                    {/* Left card — Package Info tab only */}
                    {tab === 0 && pkg && <PackageCard pkg={pkg} />}

                    {/* Right scrollable content */}
                    {tab !== 1 && <Box sx={{ flex: 1, overflowY: 'auto', px: 8, py: 3 }}>

                        {/* ── Tab 0: Package Info ──────────────────────── */}
                        {tab === 0 && (
                            <Box>
                                {/* Section heading */}
                                <Typography sx={{ fontWeight: 800, fontSize: 22, color: 'var(--brand-text, #111)', mb: 3 }}>
                                    {pkg?.rightHeading || 'What this package includes'}
                                </Typography>

                                {/* Feature grid or plain list */}
                                {pkg?.infoItems ? (
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5, mb: 1 }}>
                                        {pkg.infoItems.map((item) => (
                                            <Box key={item.title} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                                <Box sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    bgcolor: pkg?.accentColor || '#004aab',
                                                    mt: '6px',
                                                    flexShrink: 0,
                                                }} />
                                                <Box>
                                                    <Typography sx={{ fontWeight: 700, fontSize: 15, color: 'var(--brand-text, #111)', lineHeight: 1.4, mb: 0.3 }}>
                                                        {item.title}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 13.5, color: 'var(--brand-text-muted, #6B7280)', lineHeight: 1.55 }}>
                                                        {item.detail}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                ) : (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" sx={{ fontSize: 14, color: 'var(--brand-text-muted, #4B5563)', mb: 1.5, lineHeight: 1.6 }}>
                                            {pkg ? pkg.description : 'Explore what is included in your recommended package using the overview below.'}
                                        </Typography>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 15, mb: 1, color: 'var(--brand-text, #111)' }}>
                                            What this package includes
                                        </Typography>
                                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                                            {(pkg ? pkg.features : [
                                                'Assessments and scorecards',
                                                'Program creation and alignment tools',
                                                'Partner dashboards and impact reporting',
                                            ]).map((f) => (
                                                <li key={f}>
                                                    <Typography variant="body2" sx={{ fontSize: 14, color: 'var(--brand-text-muted, #4B5563)', lineHeight: 1.6 }}>{f}</Typography>
                                                </li>
                                            ))}
                                        </ul>
                                    </Box>
                                )}

                                {/* Highlight info box */}
                                {pkg?.highlightBox && (
                                    <Box sx={{
                                        mt: 2.5,
                                        bgcolor: isDark ? 'rgba(255,255,255,0.05)' : (pkg.cardBgColor || '#EEF3FF'),
                                        border: isDark ? '1px solid rgba(255,255,255,0.1)' : 'none',
                                        borderRadius: 2,
                                        px: 2.5,
                                        py: 2,
                                    }}>
                                        <Typography sx={{ fontWeight: 700, fontSize: 15, color: isDark ? 'var(--brand-text, #E5E7EB)' : (pkg?.accentColor || '#004aab'), mb: 0.5 }}>
                                            {pkg.highlightBox.title}
                                        </Typography>
                                        <Typography sx={{ fontSize: 14, color: 'var(--brand-text-muted, #6B7280)', lineHeight: 1.6 }}>
                                            {pkg.highlightBox.desc}
                                        </Typography>
                                    </Box>
                                )}

                                {/* CTA buttons — auto width, centered */}
                                {pkg?.ctaButtons && (
                                    <Box sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 1.5,
                                        mt: 3,
                                        justifyContent: pkg.ctaButtons.length === 1 ? 'flex-start' : 'flex-start',
                                    }}>
                                        {pkg.ctaButtons.map((btn) => (
                                            <Button
                                                key={btn.label}
                                                variant="contained"
                                                disableElevation
                                                sx={{
                                                    textTransform: 'none',
                                                    bgcolor: '#FF6900',
                                                    color: '#fff',
                                                    fontWeight: 700,
                                                    fontSize: 15,
                                                    px: 3,
                                                    py: 1.2,
                                                    borderRadius: 2,
                                                    minWidth: 180,
                                                    '&:hover': { bgcolor: '#E65C00' },
                                                }}
                                            >
                                                {btn.label}
                                            </Button>
                                        ))}
                                    </Box>
                                )}

                                {/* Case study link — packages without ctaButtons */}
                                {pkg && pkg.caseStudy && (
                                    <Button
                                        variant="text"
                                        size="small"
                                        sx={{ mt: 2, textTransform: 'none', color: '#FF6900', pl: 0, fontWeight: 600, fontSize: 14 }}
                                    >
                                        {pkg.caseStudy}
                                    </Button>
                                )}
                            </Box>
                        )}

                        {/* ── Tab 2: Preview Dashboard ──────────────────── */}
                        {tab === 2 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                                <img
                                    src={SampleDashboardImg}
                                    alt="Sample partner dashboard"
                                    style={{ width: '90%', borderRadius: 12, boxShadow: '0 2px 16px rgba(0,0,0,0.10)', display: 'block' }}
                                />
                            </Box>
                        )}
                    </Box>}
                </Box>

            </Box>{/* end white card */}
        </Box>
    );
};

export default CpaPackagePanel;
